import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { formatDate, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton, SkeletonFilters } from "../../ui/skeletons";
import { useMailboxes } from "../emails/queries";
import type { EmailDelivery, EmailSettings, Suppression } from "./types";

// How mail leaves the building, which is no longer a question this page asks.
//
// There used to be a transport picker here, offering SMTP and SendGrid beside a demo inbox, and it
// was inert. The Worker builds one mail transport per invocation from its own configuration and
// hands it to every job, so a workspace that filled in an SMTP host had that password validated,
// encrypted, stored, and never once used to send anything. Every workspace sends through the
// platform's Cloudflare Email Sending binding, and that cannot be a per-workspace setting even in
// principle: the provider refuses an envelope sender on any domain the platform has not verified.
//
// What remains is the part that genuinely is a choice, and it is now a choice between real things.
// The from address is a select over this workspace's own mailboxes rather than a free-text field,
// because the server validates it against messaging.mailboxes; typed anything else, it was accepted
// by the form and refused by the provider on every send afterwards.
export function EmailDeliveryPanel() {
  const email = useQuery({
    queryKey: ["messaging", "email-settings"],
    queryFn: () => api<EmailSettings>("/api/v1/messaging/email-settings"),
  });
  const deliveries = useQuery({
    queryKey: ["messaging", "deliveries"],
    queryFn: () => api<ListResponse<EmailDelivery>>("/api/v1/messaging/deliveries?limit=25"),
  });
  const suppressions = useQuery({
    queryKey: ["messaging", "suppressions"],
    queryFn: () => api<ListResponse<Suppression>>("/api/v1/messaging/suppressions"),
  });
  const mailboxes = useMailboxes();
  const [fromAddress, setFromAddress] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const mailboxItems = mailboxes.data?.items;
  const sendable = (mailboxItems ?? []).filter((item) => item.status === "active");
  // A saved sender wins, but only while it is still one of this workspace's own mailboxes. The
  // server validates the sender against messaging.mailboxes, so an address saved before mailboxes
  // existed, or one belonging to a mailbox since removed, is an address this workspace cannot send
  // from: left selected it would render a select with no matching option and be refused on save.
  // Anything else opens on the default inbox, which is the address provisioning issued.
  //
  // The dependency is the query's own array rather than the filtered copy, which would be a new
  // identity on every render and re-run this effect forever.
  useEffect(() => {
    const usable = (mailboxItems ?? []).filter((item) => item.status === "active");
    const saved = email.data?.fromAddress ?? "";
    if (saved !== "" && usable.some((item) => item.address === saved)) {
      setFromAddress(saved);
      return;
    }
    const fallback = usable.find((item) => item.isDefault) ?? usable[0];
    if (fallback) setFromAddress(fallback.address);
  }, [email.data?.fromAddress, mailboxItems]);

  async function saveEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      // No driver in the payload, and no credentials either. The server settles the transport,
      // because there is only one answer a workspace could give: it belongs to the deployment.
      await api("/api/v1/messaging/email-settings", {
        method: "PUT",
        body: jsonBody({
          fromName: String(form.get("fromName")),
          fromAddress,
          replyTo: String(form.get("replyTo")),
          version: email.data?.version ?? 0,
        }),
      });
      await email.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function testEmail() {
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/messaging/email-settings/test", { method: "POST", body: jsonBody({}) });
      await Promise.all([email.refetch(), deliveries.refetch()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function addSuppression(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/messaging/suppressions", {
        method: "POST",
        body: jsonBody({
          address: String(form.get("address")),
          reason: String(form.get("reason")),
          appliesTo: String(form.get("appliesTo")),
          source: String(form.get("source")),
        }),
      });
      formElement.reset();
      await suppressions.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function revokeSuppression(item: Suppression) {
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/messaging/suppressions/${item.id}`, { method: "DELETE" });
      await suppressions.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (email.isLoading) return <PanelSkeleton table={false} />;
  const queryError = email.error ?? deliveries.error ?? suppressions.error;
  return (
    <div className="stack">
      {error || queryError ? <ErrorPanel error={error ?? queryError} /> : null}
      <div className="content-grid">
        <Card className="span-7">
          <CardHeader
            title="Outbound email"
            description="Email is sent for you by the platform. What this sets is which of your mailboxes it comes from, and how that sender is named."
            actions={
              email.data ? (
                <StatusPill value={email.data.configured ? "configured" : "not_configured"} />
              ) : null
            }
          />
          {mailboxes.isLoading ? (
            <SkeletonFilters bare count={2} />
          ) : sendable.length === 0 ? (
            <EmptyState
              title="No mailbox yet"
              description="Your workspace address is created for you, so this is normally never empty. If it is still empty in a few minutes, contact support."
            />
          ) : (
            <form className="form-grid" onSubmit={saveEmail}>
              <Field
                label="Send from"
                hint="One of your own mailboxes. Add more under Emails, Mailboxes."
              >
                <select
                  name="fromAddress"
                  value={fromAddress}
                  onChange={(event) => setFromAddress(event.target.value)}
                  required
                >
                  {sendable.map((item) => (
                    <option key={item.id} value={item.address}>
                      {item.displayName ? `${item.displayName} (${item.address})` : item.address}
                      {item.isDefault ? " - default" : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="From name" hint="The name recipients see beside the address.">
                <input name="fromName" defaultValue={email.data?.fromName} required />
              </Field>
              <Field
                label="Reply-to"
                hint="Optional. Leave this blank to have replies come back to the mailbox above."
              >
                <input name="replyTo" type="email" defaultValue={email.data?.replyTo} />
              </Field>
              <div className="full">
                <FormActions submitting={working} submitLabel="Save email settings" />
              </div>
            </form>
          )}
        </Card>
        <Card className="span-5">
          <CardHeader
            title="Test email"
            description="Sends one message to your own address, along the same path a real one takes."
          />
          {email.data ? (
            <dl className="definition-list">
              <dt>Sending from</dt>
              <dd>{email.data.fromAddress || "Not set"}</dd>
              <dt>Last tested</dt>
              <dd>{formatDate(email.data.lastTestedAt)}</dd>
              <dt>Result</dt>
              <dd>
                <StatusPill value={email.data.lastTestStatus || "not_tested"} />
              </dd>
            </dl>
          ) : null}
          <button
            className="button secondary"
            type="button"
            disabled={working || !email.data?.configured || !email.data?.fromAddress}
            onClick={testEmail}
          >
            Send test email
          </button>
        </Card>
      </div>
      <div className="content-grid">
        <Card className="span-6">
          <CardHeader
            title="Blocked email addresses"
            description="Stop non-security email to an address after an unsubscribe, failed delivery, complaint, or staff decision."
          />
          <form className="form-stack" onSubmit={addSuppression}>
            <Field label="Email address">
              <input name="address" type="email" required />
            </Field>
            <Field label="Reason">
              <select name="reason" defaultValue="manual">
                <option value="manual">Manual</option>
                <option value="unsubscribe">Unsubscribe</option>
                <option value="bounce">Bounce</option>
                <option value="complaint">Complaint</option>
                <option value="legal">Legal</option>
              </select>
            </Field>
            <Field label="Applies to">
              <select name="appliesTo" defaultValue="all_non_security">
                <option value="all_non_security">All non-security email</option>
                <option value="marketing">Marketing only</option>
              </select>
            </Field>
            <Field label="Source">
              <input name="source" defaultValue="staff settings" required />
            </Field>
            <FormActions submitting={working} submitLabel="Block email address" />
          </form>
          {suppressions.data?.items.length ? (
            <DataTable
              caption="Blocked email addresses"
              items={suppressions.data.items}
              getKey={(item) => item.id}
              columns={[
                { label: "Reason", render: (item) => titleCase(item.reason) },
                { label: "Emails blocked", render: (item) => titleCase(item.appliesTo) },
                { label: "Source", render: (item) => item.source },
                {
                  label: "Action",
                  render: (item) =>
                    item.revokedAt ? (
                      "Revoked"
                    ) : (
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working}
                        onClick={() => revokeSuppression(item)}
                      >
                        Remove block
                      </button>
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No blocked email addresses"
              description="Addresses you block will appear here without showing the full address."
            />
          )}
        </Card>
        <Card className="span-6">
          <CardHeader title="Recent email deliveries" />
          {deliveries.data?.items.length ? (
            <DataTable
              caption="Recent email deliveries"
              items={deliveries.data.items}
              getKey={(item) => item.id}
              columns={[
                { label: "Recipient", render: (item) => item.recipient },
                { label: "Template", render: (item) => item.templateKey },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                { label: "Attempts", render: (item) => item.attempts },
              ]}
            />
          ) : (
            <EmptyState
              title="No email deliveries"
              description="Queued and sent email will appear here."
            />
          )}
        </Card>
      </div>
      <Notice tone="info">
        Your workspace sends from its own address on the platform's mail domain, and replies come
        back to that same mailbox. The wording of each message lives with the mailbox, under Emails,
        so the people who write to customers can change it without touching the connection that
        carries it. Sending from a domain you own yourself is arranged with support rather than
        here.
      </Notice>
    </div>
  );
}
